import { Box, Button, FormControl, Typography } from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { findEntityById } from '../../../../utils/EntityUtils'
import {
    fetchEntityRecordsListBySearch,
    onSaveEntityFields,
} from '../../../../services/entityServices'
import { updateLoader } from '../../../../redux/slices/loaderSlice'
import {
    AttributeType,
    EntityStatus,
    EntityStepTypes,
    UserPermissions,
} from '../../../../constants/constants'
import {
    EntityType,
    IDeviceRelationAttributeValue,
} from '../../../DTStudio/interfaces'
import SubTemplatesList from '../../../Common/SubTemplatesList'
import { fetchEntityInstanceData } from '../../../../services/entityInstanceServices'
import {
    updateAttributeValues,
    updateValuesForPayload,
} from '../../../../utils/EntityAttributesUtils'
import { store } from '../../../../redux/store'
import Fields from '../../../Common/Fields'
import { useTranslation } from 'react-i18next'
import SearchInAPIAndSelectInput from '../../../Common/SearchInAPIAndSelectInput'
// import SearchInAPIAndMultiSelectInput from '../../../Common/SearchInAPIAndMultiSelectInput'
import { validateAttributes } from '../../../../utils/attributeValidations'
import { isDevOrQAEnv } from '../../../../utils/MiscellaneousUtils'
import dayjs from 'dayjs'

interface DeviceRelationAttributeProps {
    label: string
    id: string
    value: any
    onChange: any
    errorMessage?: string
    required?: boolean
    disabled?: boolean
    templateId: string
    parentTemplateId: string
    orgId: string
    selectedEntities: any
}

interface AddNewEntityDetails {
    templateId: string
    templateName: string
    entityId: string
    attributes: any[]
}
const PAGE_SIZE = 30

const DeviceRelationAttribute = (
    props: DeviceRelationAttributeProps,
): ReactElement => {
    const {
        label,
        id,
        value,
        onChange,
        errorMessage,
        required,
        disabled,
        templateId,
        parentTemplateId,
        orgId,
        selectedEntities,
    } = props
    const { t } = useTranslation()

    const navList = useAppSelector((state) => state.nav.navList)
    const org = useAppSelector((state) => state.org.org)
    const permissions = useAppSelector((state) => state.org.permissions)
    const orgTimeZone =
        org?.preferences?.localization?.timeZone || dayjs.tz.guess()
    const entitySteps = useAppSelector(
        (state) => state.entityInstance.entitySteps,
    )
    const entityAttributeStep = entitySteps?.find(
        (i) => i.stepType === EntityStepTypes.Attributes,
    )
    const dispatch = useAppDispatch()

    const [recordsList, setRecordsList] = useState<any[]>([])
    const [selectedValues, setSelectedValues] = useState<
        IDeviceRelationAttributeValue[]
    >([])
    //if it is entity json we can add new entity in inline
    const [openSubTemplates, setOpenSubTemplates] = useState(false)
    const [newEntityDetails, setNewEntityDetails] =
        useState<AddNewEntityDetails>()
    const [searchText, setSearchText] = useState<string>('')
    const [pageNumber, setPageNumber] = useState<number>(1)
    const [hasMore, setHasmore] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState(false)
    const templateData: EntityType = useMemo(
        () =>
            findEntityById(navList, templateId || '')?.entity ||
            ({} as EntityType),
        [navList, templateId],
    )
    //adding new Device has permission only for org admin and device writer only.
    //first we need check is the relation template is device or not. then we need to check is super admin or device manager.
    const isDeviceManager = isDevOrQAEnv()
        ? true
        : templateData?.permissions?.writer?.device
          ? permissions.includes(UserPermissions.DEVICE_WRITER) ||
            permissions.includes(UserPermissions.ORG_ADMIN)
          : true //setting true if not device template

    useEffect(() => {
        if (value) {
            setSelectedValues(value as any[])
        }
    }, [value])

    useEffect(() => {
        if (newEntityDetails?.templateId) {
            onChange(id, '100%', 'width')
        } else {
            onChange(id, '', 'width')
        }
    }, [newEntityDetails])

    useEffect(() => {
        if (templateId && orgId && parentTemplateId) {
            setIsFetching(pageNumber === 1)
            const getRecords = async () => {
                try {
                    // we don't have edit now for this attribute, if we have edit. already selected Items will not come from API.
                    const relationEntityRecords =
                        await fetchEntityRecordsListBySearch(
                            orgId,
                            templateId,
                            parentTemplateId,
                            searchText?.trim(),
                            true,
                            pageNumber,
                            PAGE_SIZE,
                        )
                    setRecordsList((prev) =>
                        pageNumber === 1
                            ? relationEntityRecords
                            : [...prev, ...relationEntityRecords],
                    )
                    setHasmore(relationEntityRecords?.length === PAGE_SIZE)
                } finally {
                    setIsFetching(false)
                }
            }
            getRecords()
        }
    }, [pageNumber, searchText, templateId, orgId, parentTemplateId])

    const onSelectRecord = (id: string, item: any) => {
        onChangeRecord(id, item?.entityId ? [item] : [])
    }

    // const onSelectRecords = (id: string, items: any) => {
    //     onChangeRecord(id, items)
    // }
    const onSearch = (id: string, text: any) => {
        setSearchText(text)
        setPageNumber(1)
    }

    const handleFetchNextPage = () => {
        if (!isFetching && hasMore) {
            setPageNumber((prev) => prev + 1)
        }
    }

    const onChangeRecord = (id: string, selectedList: any[]) => {
        setSelectedValues(selectedList)
        updateValues(selectedList)
    }

    const onClickAddNewInlineEntityJson = () => {
        setOpenSubTemplates(true)
    }

    const onCloseSubTemplatePopUp = () => {
        setOpenSubTemplates(false)
    }

    const onSubmitSubTemplatePopUp = (templateDetails: {
        templateId: string
        templateName: string
    }) => {
        getAndSetTheAddOrEditEntityDetails(templateDetails)
    }

    const getAndSetTheAddOrEditEntityDetails = async (templateDetails: {
        templateId: string
        templateName: string
        entityId?: string
    }) => {
        dispatch(updateLoader(true))
        const EntityInstanceResponse: any = await fetchEntityInstanceData(
            orgId || org?.id || '',
            templateDetails?.templateId,
            templateDetails?.entityId || '',
        )
        const updatedAttributesList = updateAttributeValues(
            EntityInstanceResponse?.attributes || [],
            store,
            !!templateDetails?.entityId,
        )
        const updateNewEntityDetails: AddNewEntityDetails = {
            templateId: templateDetails?.templateId,
            templateName: templateDetails?.templateName,
            entityId: templateDetails?.entityId || '0',
            attributes: updatedAttributesList,
        }
        setNewEntityDetails(updateNewEntityDetails)

        setOpenSubTemplates(false)
        dispatch(updateLoader(false))
    }

    const onClickRecordEdit = (rec: any) => {
        getAndSetTheAddOrEditEntityDetails(rec)
    }

    const onChangeNewEntityFields = (
        id: string,
        value: string,
        key?: string,
    ) => {
        const updateNewEntityDetails = {
            ...newEntityDetails,
            attributes: newEntityDetails?.attributes.map((i) => {
                if (i.id === id) {
                    const obj = { ...i }
                    obj[key ?? 'value'] = value
                    return obj
                } else {
                    return { ...i }
                }
            }),
        }
        setNewEntityDetails(updateNewEntityDetails as AddNewEntityDetails)
    }

    const onClickSaveNewEntity = () => {
        dispatch(updateLoader(true))
        const { isValidated, updatedFields } = validateAttributes(
            newEntityDetails?.attributes || [],
        )
        if (isValidated) {
            onSaveNewEntity()
        } else {
            setNewEntityDetails({
                ...newEntityDetails,
                attributes: updatedFields || [],
            } as AddNewEntityDetails)
            dispatch(updateLoader(false))
        }
    }

    const onSaveNewEntity = async () => {
        const updatedAttibutes = await updateValuesForPayload(
            newEntityDetails?.attributes,
        )
        const sendData = [
            {
                stepType: EntityStepTypes.Attributes,
                attributes: updatedAttibutes,
            },
        ]

        const result = await onSaveEntityFields(
            sendData,
            orgId || org?.id || '',
            newEntityDetails?.templateId || '',
            newEntityDetails?.entityId || '0',
            EntityStatus.ACTIVE,
            org?.id || '',
        )
        if (result?.type == 'success') {
            const UIdAttributes = updatedAttibutes.filter(
                (i: any) => i.metadata?.Displayable && i.value,
            )
            const UId = UIdAttributes.map((item: any) => {
                if (
                    item.dataType.toLowerCase() === AttributeType.Entity_JSON ||
                    item.dataType.toLowerCase() === AttributeType.ENTITY ||
                    item.dataType.toLowerCase() === AttributeType.JSON
                ) {
                    return item.value?.uid || ''
                } else {
                    return item.value
                }
            })?.join(',')
            const addedRecordData = [
                {
                    uid: UId,
                    displayName: UId,
                    entityId: result?.payload?.entityId || '0',
                    templateId: newEntityDetails?.templateId || '',
                    templateName: newEntityDetails?.templateName || '',
                    organizatonId: orgId || org?.id,
                    organizationName: '',
                    attributes: updatedAttibutes,
                },
            ]
            onChangeRecord(id, addedRecordData)
            setNewEntityDetails(undefined)
        }
        dispatch(updateLoader(false))
    }

    const updateValues = (value: any[]) => {
        let updatedShipmentName = value?.[0]?.displayName || ''
        const isMultiuse =
            value?.[0]?.attributes?.find((i: any) => i.id === 'multiUse')
                ?.value || false

        if (isMultiuse) {
            const utcFormatted = dayjs().tz(orgTimeZone).format('YYYYMMDDHHmm')
            updatedShipmentName = updatedShipmentName + '.' + utcFormatted
        }

        onChange(id, value, 'value', 'shipmentName', updatedShipmentName)
    }

    return (
        <Box>
            <Box>
                <FormControl fullWidth disabled={!!disabled} size="small">
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: ' 100%',
                                maxWidth: `${newEntityDetails ? 'calc(50% - 20px)' : '100%'}`,
                            }}
                        >
                            {/* keeping it for future use */}
                            {/* we are not using multi select for tripnumber select */}
                            {/* {isArray ? (
                                <SearchInAPIAndMultiSelectInput
                                    id={id}
                                    label={label}
                                    placeholder={t('selectLabel', { label })}
                                    required={required}
                                    value={selectedValues}
                                    onSelect={onSelectRecords}
                                    list={recordsList || []}
                                    errorMessage={
                                        !selectedValues.length && errorMessage
                                            ? errorMessage
                                            : ''
                                    }
                                    disabled={
                                        disabled ||
                                        (selectedValues?.length > 1 &&
                                            !!entityAttributeStep?.instanceId)
                                    }
                                    hasAddNew={isDeviceManager}
                                    onSearch={onSearch}
                                    onFetchNextPage={handleFetchNextPage}
                                    hasMore={hasMore}
                                    isFetching={isFetching}
                                    itemId="entityId"
                                    itemName="displayName"
                                    onClickAddNew={() =>
                                        onClickAddNewInlineEntityJson()
                                    }
                                    onClickRowIcon={(r: any) =>
                                        onClickRecordEdit(r)
                                    }
                                    hasItemsEdit={isDeviceManager}
                                />
                            ) : ( */}
                            <SearchInAPIAndSelectInput
                                id={id}
                                label={label}
                                placeholder={t('selectLabel', { label })}
                                value={selectedValues[0] || ''}
                                list={recordsList}
                                required={required}
                                disabled={
                                    disabled ||
                                    (selectedEntities?.length > 0 &&
                                        !!entityAttributeStep?.instanceId)
                                }
                                onSelect={onSelectRecord}
                                onSearch={onSearch}
                                errorMessage={errorMessage}
                                onFetchNextPage={handleFetchNextPage}
                                hasMore={hasMore}
                                isFetching={isFetching}
                                hasAddNew={isDeviceManager}
                                itemId="entityId"
                                itemName="displayName"
                                onClickAddNew={() => {
                                    onClickAddNewInlineEntityJson()
                                }}
                                onClickRowIcon={(r: any) =>
                                    onClickRecordEdit(r)
                                }
                                hasItemsEdit={isDeviceManager}
                            />
                            {/* )} */}
                        </Box>
                    </Box>
                </FormControl>
            </Box>
            {newEntityDetails && (
                <Box
                    className="childWidth100"
                    sx={{
                        background: '#00000010',
                        marginRight: '10px',
                        marginTop: '10px',
                        padding: '20px 0px 20px 20px',
                        borderRadius: '3px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ marginBottom: '15px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#000',
                            }}
                        >
                            {t('add')} {newEntityDetails?.templateName}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                    >
                        <Fields
                            fields={newEntityDetails?.attributes}
                            onChangeFields={onChangeNewEntityFields}
                            onClickMap={() => {}}
                            disabled={false}
                            orgId={orgId}
                        />
                    </Box>
                    <Box sx={{ marginLeft: 'auto', marginRight: '20px' }}>
                        <Button
                            onClick={() => {
                                setNewEntityDetails(undefined)
                            }}
                            sx={{
                                fontSize: '12px',
                                padding: '7px 20px',
                                marginRight: '15px',
                            }}
                            variant="outlined"
                            id="inline-entity-cancel"
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => onClickSaveNewEntity()}
                            sx={{
                                width: '100px',
                                color: '#fff',
                                fontSize: '12px',
                                padding: '7px 20px',
                            }}
                            id="inline-entity-save"
                        >
                            {t('save')}
                        </Button>
                    </Box>
                </Box>
            )}
            {openSubTemplates && (
                <SubTemplatesList
                    templateId={templateId}
                    isOpen={openSubTemplates}
                    onClose={onCloseSubTemplatePopUp}
                    onSubmit={onSubmitSubTemplatePopUp}
                    submitIfSingle={true}
                    orgId={orgId}
                />
            )}
        </Box>
    )
}

export default DeviceRelationAttribute
